/**
 * Closure history — master spec §5.5, §6.14.
 *
 * The current closure is the row with the highest `closure_sequence`. There is
 * no `is_current` flag and no supersession UPDATE.
 *
 * `opportunity_closures.outcome` is authoritative for all win/loss reporting
 * because it survives reopen; `opportunities.status` is authoritative for
 * pipeline membership.
 */

import { z } from 'zod';
import { contentSourceSchema, outcomeSchema } from './enums.js';
import { dataEnvelope } from './http.js';
import { dateString, money, shortText, timestampString, uuid } from './fields.js';

export const closureRowSchema = z.object({
  closure_id: uuid,
  opportunity_id: uuid,
  closure_sequence: z.number().int().positive(),
  outcome: outcomeSchema,
  close_date: dateString,
  /** Always on the NET basis (§5.5 [R3] point 1). */
  actual_annual_revenue: money.nullable(),
  /** The factor that produced it. `rep_firms.net_factor` is mutable by design. */
  net_factor_at_close: z.string().nullable(),
  primary_reason_code: z.string(),
  primary_reason_label: z.string(),
  secondary_reason_codes: z.array(z.string()),
  competitor_id: uuid.nullable(),
  competitor_part_number: z.string().nullable(),
  price_delta_pct: z.string().nullable(),
  what_would_have_changed_it: z.string().nullable(),
  lessons_learned: z.string().nullable(),
  recorded_by: uuid,
  recorded_at: timestampString,
  content_source: contentSourceSchema,

  /** `closure_sequence = max(...)` over the opportunity. */
  is_latest: z.boolean(),
  /** `is_latest` AND the opportunity is back to `open`/`on_hold` — no current outcome. */
  is_superseded_by_reopen: z.boolean(),
  /** §6.14: the won closure with the lowest `close_date`. What a design win is counted by. */
  is_first_win: z.boolean(),
});

export type ClosureRow = z.infer<typeof closureRowSchema>;

export const closureListResponseSchema = dataEnvelope(
  z.object({ items: z.array(closureRowSchema) }),
);

/**
 * `POST /api/admin/closures/:id/redact` — the narrow append-only exception
 * (§5.5 [R4]).
 *
 * It nulls **only** `lessons_learned` and `what_would_have_changed_it`, on one
 * row, requires `Admin`, and is audited. Every other column and every `DELETE`
 * still raise, in the database as well as here.
 *
 * The audit row records **the fact, never the text**: `audit_log` is itself
 * append-only with five-year retention that §8c's erasure cannot reach, so an
 * ordinary audit row would copy the controlled parameter out of one permanent
 * table into another and the feature would achieve exactly nothing.
 */
export const redactClosureSchema = z.object({
  reason: shortText,
});

// ---------------------------------------------------------------------------
// GET /:id/history — stage history + audit log + closures, merged
// ---------------------------------------------------------------------------

export const historyEntrySchema = z.object({
  kind: z.enum(['stage', 'audit', 'closure']),
  at: timestampString,
  actor_user_id: uuid.nullable(),
  actor_name: z.string().nullable(),
  summary: z.string(),
  detail: z.record(z.unknown()).nullable(),
});

export type HistoryEntry = z.infer<typeof historyEntrySchema>;

export const historyResponseSchema = dataEnvelope(z.object({ items: z.array(historyEntrySchema) }));
